import { useFormik } from "formik"
import PropTypes from "prop-types"
import React, { useState } from "react"
import { Link, useHistory, withRouter } from "react-router-dom"
import {
  Alert,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap"
import * as Yup from "yup"
import profile from "assets/images/login.svg"
import { faEye } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import QrCodeDisplay from "components/QrCodeDisplay/QrCodeDisplay"
import toast from "components/Toast/Toast"
import jwt from "jsonwebtoken"
import { withTranslation } from "react-i18next"
import { authProtectedRoutes } from "routes"
import { postLogin, postVerify2MFA } from "services/auth/calls"
import { BgAnimado } from "./Components/BgAnimado/BgAnimado"
import { clientCapitalized } from "assets"

const eye = <FontAwesomeIcon icon={faEye} />

const Login = props => {
  const { t } = props
  const [isLoading, setIsLoading] = useState(false)
  document.title = `Login | Sensetek ${clientCapitalized}`

  const [passwordShown, setPasswordShown] = useState(false)

  const [showModalQrcode, setShowModalQrcode] = useState(false)
  const [qrCodeUrl, setQrCodeUrl] = useState("")
  const [tempToken, setTempToken] = useState("")
  const [userId, setUserId] = useState(null)

  const [showModal2MFA, setShowModal2MFA] = useState(false)

  const history = useHistory()

  const togglePassword = () => {
    setPasswordShown(!passwordShown)
  }

  const validation2mfa = useFormik({
    enableReinitialize: true,
    initialValues: {
      token: "",
    },
    validationSchema: Yup.object({
      token: Yup.string().required(t("Token de autenticador é obrigatório")),
    }),
    onSubmit: async values => {
      try {
        setIsLoading(true)
        const { payload } = await postVerify2MFA({
          userId: userId,
          token: values.token,
          temporaryToken: tempToken,
        })
        localStorage.setItem("token", payload.accessToken)
        const loggedUser = payload.user

        if (loggedUser.isTempPassword) {
          history.push("/password/update")
        } else {
          const roles = jwt.decode(payload.accessToken)?.roles

          if (roles?.length === 0) {
            history.push("/no-profiles")
          } else {
            try {
              const paths = authProtectedRoutes.filter(apr =>
                apr?.requiredRoles?.includes(roles[0]) ? true : false
              )
              history.push(paths[0].path)
            } catch (error) {
              console.error(error)
            }
          }
        }
      } catch (error) {
        console.error(error)
        toast("warning", error?.message)
      } finally {
        setIsLoading(false)
      }
    },
  })

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      user: "",
      password: "",
    },
    validationSchema: Yup.object({
      user: Yup.string().required(t("LoginUserVerification")),
      password: Yup.string().required(t("LoginPassVerification")),
    }),
    onSubmit: async values => {
      try {
        setIsLoading(true)
        setError(null)
        localStorage.removeItem("token")
        const { payload } = await postLogin({
          login: values.user,
          password: values.password,
        })

        if (payload?.accessToken) {
          localStorage.setItem("token", payload.accessToken)
          const loggedUser = payload.user

          if (loggedUser.isTempPassword) {
            history.push("/password/update")
          } else {
            const roles = jwt.decode(payload.accessToken)?.roles

            if (roles?.length === 0) {
              history.push("/no-profiles")
            } else {
              try {
                const paths = authProtectedRoutes.filter(apr =>
                  apr?.requiredRoles?.includes(roles[0]) ? true : false
                )

                history.push(paths[0].path)
              } catch (error) {
                console.error(error)
              }
            }
          }
          return
        } else {
          console.log(payload)
          const { code, qrCode, status, token } = payload
          if (code == 10004) {
            setQrCodeUrl(qrCode)
            setShowModalQrcode(true)
            setIsLoading(false)
            setTempToken(token)
            return
          }
          if (code == 10005) {
            setUserId(token?.userId)
            setTempToken(token?.token)
            setShowModal2MFA(true)
            return
          }
        }
      } catch (error) {
        setError(
          "Usuário e/ou Senha inválidos. Por favor entre com Usuário e Senha corretos."
        )
        console.error(error)
        toast(
          "warning",
          "Usuário e/ou Senha inválidos. Por favor entre com Usuário e Senha corretos.",
          "Aviso"
        )
      } finally {
        setIsLoading(false)
      }
    },
  })

  const [error, setError] = useState()

  return (
    <React.Fragment>
      <div
        className="account-pages my-5 pt-sm-5"
        style={{
          position: "absolute",
          left: "0",
          right: "0",
          top: "0",
          bottom: "0",
          margin: "auto",
          zIndex: "100",
        }}
      >
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className="overflow-hidden">
                <div className="bg-primary bg-soft">
                  <Row>
                    <Col className="col-5 align-self-end">
                      <img src={profile} alt="" className="img-fluid" />
                    </Col>
                  </Row>
                </div>
                <CardBody className="pt-0">
                  {/* <div className="avatar-md profile-user-wid mb-4 ms-4">
                    <span className="avatar-title rounded-circle bg-white">
                      <img
                        src={logo}
                        alt=""
                        height="60"
                        className="ms-4 mt-4"
                      />
                    </span>
                  </div> */}
                  <div className="p-2">
                    <Form
                      className="form-horizontal"
                      onSubmit={e => {
                        e.preventDefault()
                        validation.handleSubmit()
                        return false
                      }}
                    >
                      {error ? <Alert color="danger">{error}</Alert> : null}

                      <div className="mb-3">
                        <Label className="form-label">{t("Usuario")}</Label>
                        <Input
                          name="user"
                          className="form-control"
                          placeholder={t("Usuario")}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.user || ""}
                          invalid={
                            validation.touched.user && validation.errors.user
                              ? true
                              : false
                          }
                        />
                        {validation.touched.user && validation.errors.user ? (
                          <FormFeedback type="invalid">
                            {validation.errors.user}
                          </FormFeedback>
                        ) : null}
                      </div>

                      <div>
                        <Label className="form-label">{t("Senha")}</Label>
                        <InputGroup>
                          <Input
                            name="password"
                            value={validation.values.password || ""}
                            type={passwordShown ? "text" : "password"}
                            placeholder={t("Senha")}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={
                              validation.touched.password &&
                              validation.errors.password
                                ? true
                                : false
                            }
                          />
                          <InputGroupText>
                            <i onClick={togglePassword}>{eye}</i>
                          </InputGroupText>
                          {validation.touched.password &&
                          validation.errors.password ? (
                            <FormFeedback type="invalid">
                              {validation.errors.password}
                            </FormFeedback>
                          ) : null}
                        </InputGroup>
                      </div>

                      {isLoading && (
                        <div className="mt-3" style={{ textAlign: "center" }}>
                          <Spinner className="ms-2" color="primary" />
                        </div>
                      )}
                      {!isLoading && (
                        <div className="mt-3 d-grid">
                          <button
                            className="btn btn-primary btn-block"
                            type="submit"
                            disabled={isLoading}
                          >
                            {t("Entrar")}
                          </button>
                        </div>
                      )}
                    </Form>
                    <div className="mt-4 text-center">
                      <Link to="/redefinir-senha" className="text-muted">
                        <i className="mdi mdi-lock me-1" />
                        {t("Esqueceu senha?")}
                      </Link>
                    </div>
                  </div>
                </CardBody>
              </Card>
              <div className="mt-5 text-center text-white">
                <p>{`SenseTek - ${t("DireitosReservados")}`}</p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
      <BgAnimado />
      <Modal
        isOpen={showModalQrcode}
        centered
        toggle={() => {
          setShowModalQrcode(!showModalQrcode)
        }}
      >
        <ModalHeader>
          Cadastro de Autenticação de 2 fatores
          <button
            type="button"
            onClick={() => {
              setShowModalQrcode(false)
            }}
            className="close"
            data-dismiss="modal"
            aria-label="Close"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col>
              Seu usuário deve cadastrar um autenticador Google para poder
              prosseguir. Utilize o QRCODE a baixo para configurar seu
              autenticador.
            </Col>
          </Row>
          <Row>
            <Col className="text-center">
              <QrCodeDisplay otpauthUrl={qrCodeUrl} />
            </Col>
          </Row>
          <Row>
            <Col>Este QRCODE possui uma validade de 5 minutos.</Col>
          </Row>
        </ModalBody>
      </Modal>
      <Modal
        isOpen={showModal2MFA}
        centered
        toggle={() => {
          setShowModal2MFA(!showModal2MFA)
          validation2mfa.resetForm()
        }}
      >
        <ModalHeader>
          Autenticação de 2 fatores
          <button
            type="button"
            onClick={() => {
              setShowModal2MFA(false)
              validation2mfa.resetForm()
            }}
            className="close"
            data-dismiss="modal"
            aria-label="Close"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </ModalHeader>
        <ModalBody>
          <Form
            className="form-horizontal"
            onSubmit={e => {
              e.preventDefault()
              validation2mfa.handleSubmit()
              return false
            }}
          >
            <Row>
              <Col>
                <div className="mb-3">
                  <Label className="form-label">{t("Token")}</Label>
                  <Input
                    name="token"
                    className="form-control"
                    placeholder={t("")}
                    onChange={validation2mfa.handleChange}
                    onBlur={validation2mfa.handleBlur}
                    value={validation2mfa.values.token || ""}
                    invalid={
                      validation2mfa.touched.token &&
                      validation2mfa.errors.token
                        ? true
                        : false
                    }
                  />
                  {validation2mfa.touched.token &&
                  validation2mfa.errors.token ? (
                    <FormFeedback type="invalid">
                      {validation2mfa.errors.token}
                    </FormFeedback>
                  ) : null}
                </div>

                {isLoading && (
                  <div className="mt-3" style={{ textAlign: "center" }}>
                    <Spinner className="ms-2" color="primary" />
                  </div>
                )}
                {!isLoading && (
                  <div className="mt-3 d-grid">
                    <button className="btn btn-primary btn-block" type="submit">
                      {t("Autenticar")}
                    </button>
                  </div>
                )}
              </Col>
            </Row>
          </Form>
        </ModalBody>
      </Modal>
    </React.Fragment>
  )
}

export default withRouter(withTranslation()(Login))

Login.propTypes = {
  t: PropTypes.any,
}
